<template>
  <form-wrapper
    full-width
    full-height
  >
    <template v-slot:body>
      <paper-simple class="h-100 m-auto">
        <p>
          A role contains a set of permissions that allows you to perform
          specific actions on Captain resources. To make permissions available
          to users, you grant roles to the members.
        </p>
        <ul>
          <li class="mt-1">
            <span class="text-thicker">
              Owner:
            </span>
            The owner of the organization with all editor permissions and
            permissions for the following actions: Manage roles and permissions
            for the organization and all resources within the organization. Set
            up billing for the organization.
          </li>
          <li class="mt-1">
            <span class="text-thicker">
              Editor:
            </span>
            All viewer permissions, plus permissions for actions that modify
            state, such as changing existing resources.
          </li>
          <li class="mt-1">
            <span class="text-thicker">
              Viewer:
            </span>
            Permissions for read-only actions that do not affect the state, such
            as viewing (but not modifying) existing resources or data.
          </li>
        </ul>

        <div class="users">
          <validation-observer
            ref="addUser"
            tag="form"
          >
            <div class="col-12 col-md-8 m-auto">
              <div class="d-flex no-icon">
                <div class="w-50">
                  <label>
                    Email
                  </label>
                </div>
                <div class="w-50">
                  <label>
                    Role
                  </label>
                </div>
              </div>
              <template v-for="email in emails">
                <div
                  :key="email.index"
                  class="d-flex mt-1 position-relative"
                >
                  <validation-provider
                    #default="{ errors }"
                    :vid="`email${email.index}`"
                    name="Email"
                    rules="required|email"
                    class="w-50 icon-left"
                  >
                    <b-input
                      :id="`email${email.index}`"
                      v-model="email.email"
                      :state="errors.length > 0 ? false : null"
                      placeholder="Enter user’s email..."
                      class="mb-0"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                  <validation-provider
                    #default="{ errors }"
                    name="Role"
                    :vid="`role${email.index}`"
                    rules="required"
                    class="w-50"
                  >
                    <b-spinner v-if="fetchingRoles" />

                    <b-form-select
                      v-else
                      :id="`role${email.index}`"
                      v-model="email.role"
                      :state="errors.length > 0 ? false : null"
                      class="mb-0 mr-0"
                      :options="roleOptions"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                  <a
                    class="icon-right pt-10"
                    @click="onRemoveField(email.index)"
                  >
                    <GjIcon name="Delete" />
                  </a>
                </div>
              </template>
              <div class="px-1 px-md-2 mt-1">
                <a
                  class="text-primary text-medium"
                  @click="onAddField"
                >
                  + Add user
                </a>
              </div>
            </div>
          </validation-observer>
        </div>
      </paper-simple>
    </template>
    <template v-slot:footer>
      <b-button
        variant="bg-white"
        @click="onCancel()"
      >
        Cancel
      </b-button>
      <button-loading
        variant="bg-primary"
        class="ml-1"
        :spinning="addingUser"
        @click="validation()"
      >Save</button-loading>
    </template>
  </form-wrapper>
</template>

<script>
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { email, required } from '@validations'

export default {
  name: 'AddUser',
  components: { ValidationProvider, ValidationObserver },
  data() {
    return {
      loading: true,
      emails: [
        {
          index: 0,
          id: 0,
          email: '',
          role: null,
        },
      ],
      roleOptions: [{ value: null, text: 'Select' }],
      email,
      required,
    }
  },
  computed: {
    lastIndex() {
      return this.emails[this.emails.length - 1]?.index || 0
    },
    firstIndex() {
      return this.emails[0]?.index || 0
    },
    rolesInvalid() {
      return this.emails.some(
        role => role.email.length > 0 && role.role === null,
      )
    },
    addingUser() {
      return this.$store.state.organization.addingUser
    },
    fetchingRoles() {
      return this.$store.state.roles.fetchingRoles
    },
  },
  created() {
    this.$store
      .dispatch('roles/fetchRoles', {
        pageIndex: 0,
        pageSize: 500,
      })
      .then(() => {
        const role = this.$store.state.roles.roles

        for (let i = 0; i < role.length; i++) {
          this.roleOptions.push({ value: role[i].id, text: role[i].name })
        }
      })
      .catch(error => {
        this.errorToast(error.response?.data?.errors[0] || 'Something went wrong while fetching roles.')
      })
  },
  methods: {
    validation() {
      return new Promise((resolve, reject) => {
        this.$refs.addUser.validate().then(success => {
          if (success) {
            resolve(true)
            this.onSave()
          } else {
            reject()
          }
        })
      })
    },
    onSave() {
      for (let i = 0; i < this.emails.length; i++) {
        const user = {
          email: this.emails[i].email,
          roleId: this.emails[i].role,
        }
        this.$store
          .dispatch('organization/inviteUser', user)
          .then(() => {
            this.successToast('User was invited')
            if (i === this.emails.length - 1) {
              this.$router.push({
                name: 'organization-settings',
                query: { tab: '1' },
              })
            }
          })
          .catch(error => {
            this.errorToast(error.response?.data?.errors[0] || 'Something went wrong while inviting user.')
          })
      }
    },
    onCancel() {
      this.$router.push({ name: 'organization-settings', query: { tab: '1' } })
    },
    onAddField() {
      this.emails.push({
        index: this.lastIndex + 1,
        email: '',
        role: null,
      })
    },
    onRemoveField(index) {
      this.emails = this.emails.filter(obj => obj.index !== index)

      for (let i = 0; i < this.emails.length; i++) {
        this.emails[i].index = i
      }
    },
    validEmail(string) {
      return string.length > 0
    },
  },
}
</script>

<style lang="scss" scoped>
::v-deep {
  .paper {
    @media (min-width: 1200px) {
      width: 75%;
    }
  }
}
.users {
  border-top: 1px solid #e0e5eb;
  padding-top: 24px;
  .no-icon {
    margin-right: 34px;
  }
  .spinner-border {
    width: 1.5rem;
    margin-top: 7px;
    height: 1.5rem;
  }
  .pt-10 {
    padding-top: 10px;
  }
}
</style>
